/** How a frame is written: the capture profile of the server says which. */
export interface EncodeOptions {
  encoding: 'jpeg' | 'png'
  /** Flip left to right (the profile asks for it for providers that expect a mirror picture). */
  mirror: boolean
}

export const JPEG_QUALITY = 0.92
/** The server refuses frames wider or taller than 4096 px; the plan keeps them under 1200 x 1600. */
export const MAX_SHORT_SIDE = 1200
export const MAX_LONG_SIDE = 1600

/** The size of the frame: the camera's size, shrunk to fit 1200 x 1600 (short x long side), never enlarged. */
export function fitSize(width: number, height: number): { width: number; height: number } {
  const short = Math.min(width, height)
  const long = Math.max(width, height)
  const scale = Math.min(1, MAX_SHORT_SIDE / short, MAX_LONG_SIDE / long)
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) }
}

/**
 * One picture of the video as a Blob. The canvas lives only inside this call: it is cleared and shrunk before the
 * function returns, so no copy of the picture stays in the page (nothing is written to any browser storage).
 */
export async function encodeFrame(video: HTMLVideoElement, opts: EncodeOptions): Promise<Blob> {
  const { width, height } = fitSize(video.videoWidth || 640, video.videoHeight || 480)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas_unavailable')
  try {
    if (opts.mirror) {
      ctx.translate(width, 0)
      ctx.scale(-1, 1)
    }
    ctx.drawImage(video, 0, 0, width, height)
    const type = opts.encoding === 'png' ? 'image/png' : 'image/jpeg'
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode_failed'))), type, JPEG_QUALITY)
    })
  } finally {
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.clearRect(0, 0, width, height)
    canvas.width = 0
    canvas.height = 0
  }
}
